/**
 * MarketDataContext — Global real-time market data via Cloudflare Durable Object WebSocket
 *
 * Data flow:
 *   Cloudflare DO (MarketDataHubDO) → WebSocket → MarketDataContext → components
 *
 * The DO fetches live prices from Yahoo Finance and broadcasts synthetics
 * to all connected frontend clients every 3 seconds.
 */
import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import { useAuth } from './AuthContext'

export interface MarketTick {
  token: string           // e.g. "101999972"
  name: string            // e.g. "Nifty Bank"
  ltp: number             // last traded price
  open: number
  high: number
  low: number
  close: number           // previous close
  change: number          // absolute change from prev close
  pChange: number         // % change
  bid: number
  ask: number
  bidQty: number
  askQty: number
  volume: number
  oi: number              // open interest (0 for indices)
  atp: number             // average traded price
  type: 'index' | 'marketPicture' | 'ltpinfo' | 'unknown'
  exchange: string
  updatedAt: number       // Date.now()
  // level2 market depth (marketPicture only)
  depth?: Array<{ bid: { price: number; qty: number; no: number }; ask: { price: number; qty: number; no: number } }>
}

interface MarketDataContextValue {
  ticks: Record<string, MarketTick>        // token → latest tick
  connected: boolean                        // WebSocket connection status
  tickCount: number                         // total ticks received
  subscribe: (tokens: string[], type?: 'index' | 'marketPicture' | 'ltpinfo') => Promise<void>
  unsubscribe: (tokens: string[], type?: string) => Promise<void>
}

const MarketDataContext = createContext<MarketDataContextValue>({
  ticks: {},
  connected: false,
  tickCount: 0,
  subscribe: async () => {},
  unsubscribe: async () => {},
})

// Map index names from the DO to Greeksoft-style numeric tokens used by MarketTicker
export const NAME_TO_TOKEN: Record<string, string> = {
  'NIFTY': '101999957',
  'BANKNIFTY': '101999972',
  'FINNIFTY': '101999923',
  'SENSEX': '100000034',
  'INDIAVIX': '999920005',
  'BANKEX': '100000035',
  'MIDCPNIFTY': '101999999',
}

function synthToTicks(synthetics: Record<string, any>): Record<string, MarketTick> {
  const result: Record<string, MarketTick> = {}
  for (const [key, data] of Object.entries(synthetics)) {
    const token = NAME_TO_TOKEN[key]
    if (!token) continue
    result[token] = {
      token,
      name: key,
      ltp: data.spot || 0,
      open: 0,
      high: 0,
      low: 0,
      close: data.prevClose || 0,
      change: data.change || 0,
      pChange: data.changePct || 0,
      bid: 0,
      ask: 0,
      bidQty: 0,
      askQty: 0,
      volume: 0,
      oi: 0,
      atp: 0,
      type: 'index',
      exchange: key === 'SENSEX' || key === 'BANKEX' ? 'BSE' : 'NSE',
      updatedAt: Date.now(),
      // Enriched fields from VPS (for LiveWatch / Reports)
      baseAtm: data.baseAtm,
      cePremium: data.cePremium,
      pePremium: data.pePremium,
      straddlePrice: data.straddlePrice,
      syntheticSpot: data.syntheticSpot,
      syntheticAtm: data.syntheticAtm,
    } as any
  }
  return result
}

export function MarketDataProvider({ children }: { children: React.ReactNode }) {
  const { token } = useAuth()
  const [ticks, setTicks] = useState<Record<string, MarketTick>>({})
  const [connected, setConnected] = useState(false)
  const [tickCount, setTickCount] = useState(0)
  const wsRef = useRef<WebSocket | null>(null)

  useEffect(() => {
    let reconnectTimeout: ReturnType<typeof setTimeout>
    let isCancelled = false

    const connectWS = () => {
      if (isCancelled) return
      try {
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
        const apiHost = import.meta.env.VITE_API_URL
          ? new URL(import.meta.env.VITE_API_URL).host
          : window.location.host

        const ws = new WebSocket(`${wsProtocol}//${apiHost}/api/marketdata/ws`)
        wsRef.current = ws

        ws.onopen = () => {
          setConnected(true)
        }

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data)
            if (msg.type === 'initial_state' || msg.type === 'synthetics_update') {
              const synthetics = msg.synthetics || msg.data
              if (synthetics) {
                const newTicks = synthToTicks(synthetics)
                setTicks(prev => ({ ...prev, ...newTicks }))
                setTickCount(c => c + 1)
              }
            }
          } catch (e) {
            console.error('[MarketData] WS parse error', e)
          }
        }

        ws.onerror = () => {
          setConnected(false)
        }

        ws.onclose = () => {
          setConnected(false)
          wsRef.current = null
          if (!isCancelled) {
            reconnectTimeout = setTimeout(connectWS, 3000)
          }
        }
      } catch (err) {
        console.error('[MarketData] WS connection error', err)
      }
    }

    connectWS()

    return () => {
      isCancelled = true
      clearTimeout(reconnectTimeout)
      if (wsRef.current) wsRef.current.close()
    }
  }, [])

  // subscribe / unsubscribe are no-ops for now since DO handles everything
  const subscribe = useCallback(async () => {}, [])
  const unsubscribe = useCallback(async () => {}, [])

  return (
    <MarketDataContext.Provider value={{ ticks, connected, tickCount, subscribe, unsubscribe }}>
      {children}
    </MarketDataContext.Provider>
  )
}

export function useMarketData() {
  return useContext(MarketDataContext)
}

// Convenience hook for a single token's tick
export function useTick(token: string): MarketTick | null {
  const { ticks } = useMarketData()
  return ticks[token] ?? null
}
